import Vue from 'vue'
import Router from 'vue-router'
import store from './store/store'
import getUseServer from '@/utils/getServer'
import {isPermission} from '@/utils/utils'

Vue.use(Router)

export const isLeadingSolutionsDocumentation = (process.env.VUE_APP_API_DOCUMENTATION || '').toLowerCase() === 'leadingsolutions'
export const isLeadingSolutionsBrand = (process.env.VUE_APP_BRAND || '').toLowerCase() === 'leadingsolutions'

const PAGE_LAYOUTS = {
    path: '',
    component: () => import('@/layouts/full-page/FullPage.vue'),
    children: [
        // =============================================================================
        // PAGES
        // =============================================================================
        {
            path: '',
            name: 'root'
        },
        {
            path: 'login',
            name: 'login',
            component: () => import('@/views/pages/Login.vue')
        },
        {
            path: 'error-404',
            name: 'error-404',
            component: () => import('@/views/pages/Error404.vue')
        },
        {
            path: 'api/:hash',
            name: 'api',
            component: () => import(`@/views/${isLeadingSolutionsDocumentation ? 'documentationNexus' : 'documentation'}/Documentation.vue`),
            children: [
                {
                    path: ':api/:method',
                    component: () => import(`@/views/${isLeadingSolutionsDocumentation ? 'documentationNexus' : 'documentation'}/view/Main`)
                }
            ]
        },
    ]
}

const MAIN_LAYOUT = {
    path: '/',
    component: () => import('@/layouts/main/Main.vue'),
    children: [
        // =============================================================================
        // Theme Routes
        // =============================================================================
        {
            path: 'home',
            name: 'home',
            meta: {
                pageTitle: 'Home',
            },
            component: () => import('@/views/Home.vue')
        },
        {
            path: 'statistic/loads',
            name: 'statistic-loads',
            meta: {
                permissionName: 'loads',
                breadcrumb: [
                    {title: 'Home', url: '/home'},
                    {title: 'Statistic'},
                    {title: 'Loads', url: 'statistic/loads', active: true}
                ],
                pageTitle: 'Loads'
            },
            component: () => import('@/views/statistics/loads/Loads.vue')
        },
        {
            path: 'statistic/recycle',
            name: 'statistic-recycle',
            meta: {
                permissionName: 'recycle_leads',
                breadcrumb: [
                    {title: 'Home', url: '/home'},
                    {title: 'Statistic'},
                    {title: 'Recycle', url: 'statistic/recycle', active: true}
                ],
                pageTitle: 'Recycle'
            },
            component: () => import('@/views/statistics/recycle/Recycle.vue')
        },
        {
            path: 'statistic/uploads',
            name: 'statistic-uploads',
            meta: {
                permissionName: 'uploads,uploads_falcon',
                breadcrumb: [
                    {title: 'Home', url: '/home'},
                    {title: 'Statistic'},
                    {title: 'Uploads', url: 'statistic/uploads', active: true}
                ],
                pageTitle: 'Uploads'
            },
            component: () => import('@/views/statistics/uploads/Uploads.vue')
        },
        {
            path: 'statistic/advertiser',
            name: 'statistic-advertiser',
            meta: {
                permissionName: 'statistic_advertiser',
                breadcrumb: [
                    {title: 'Home', url: '/home'},
                    {title: 'Statistic'},
                    {title: 'Advertiser', url: 'statistic/advertiser', active: true}
                ],
                pageTitle: 'Advertiser'
            },
            component: () => import('@/views/statistics/AdvertiserStatistic.vue')
        },
        {
            path: 'statistic/affiliate',
            name: 'statistic-affiliate',
            meta: {
                permissionName: 'statistic_affiliate',
                breadcrumb: [
                    {title: 'Home', url: '/home'},
                    {title: 'Statistic'},
                    {title: 'Affiliate', url: 'statistic/affiliate', active: true}
                ],
                pageTitle: 'Affiliate'
            },
            component: () => import('@/views/statistics/AffiliateStatistic.vue')
        },
        {
            path: 'offers',
            name: 'statistic-offer',
            meta: {
                permissionName: 'statistic_offer',
                breadcrumb: [
                    {title: 'Home', url: '/home'},
                    {title: 'Offers', url: 'offers', active: true}
                ],
                pageTitle: 'Offers'
            },
            component: () => import('@/views/offers/Offers.vue')
        },
        {
            path: 'settings/teams',
            name: 'teams',
            meta: {
                permissionName: 'settings_team',
                breadcrumb: [
                    {title: 'Home', url: '/home'},
                    {title: 'Settings'},
                    {title: 'Settings_Teams', url: 'settings/teams', active: true}
                ],
                pageTitle: 'Settings_Teams'
            },
            component: () => import('@/views/settings/teams/TeamList.vue')
        },
        {
            path: 'settings/advertisers',
            name: 'advertisers',
            meta: {
                permissionName: 'settings_advertiser',
                breadcrumb: [
                    {title: 'Home', url: '/home'},
                    {title: 'Settings'},
                    {title: 'Advertiser', url: 'settings/advertisers', active: true}
                ],
                pageTitle: 'Advertiser'
            },
            component: () => import('@/views/settings/advertisers/AdvertiserList.vue')
        },
        {
            path: 'settings/moderation',
            name: 'moderation',
            meta: {
                permissionName: 'settings_moderation',
                breadcrumb: [
                    {title: 'Home', url: '/home'},
                    {title: 'Settings'},
                    {title: 'Moderation', url: 'settings/moderation', active: true}
                ],
                pageTitle: 'Moderation'
            },
            component: () => import('@/views/settings/moderation/Moderation.vue')
        },
        {
            path: 'settings/offers',
            name: 'settings-offers',
            meta: {
                permissionName: 'offer',
                breadcrumb: [
                    {title: 'Home', url: '/home'},
                    {title: 'Settings'},
                    {title: 'Offers', url: 'settings/offer', active: true}
                ],
                pageTitle: 'Offers'
            },
            component: () => import('@/views/settings/offers/OffersList.vue')
        },
        {
            path: 'settings/permissions',
            name: 'settings-permissions',
            meta: {
                permissionName: 'permission',
                breadcrumb: [
                    {title: 'Home', url: '/home'},
                    {title: 'Settings'},
                    {title: 'Permissions', url: 'settings/permissions', active: true}
                ],
                pageTitle: 'Permissions'
            },
            component: () => import('@/views/settings/permissions/Permissions.vue')
        },
        {
            path: 'settings/caps',
            name: 'caps',
            meta: {
                permissionName: 'settings_cap',
                breadcrumb: [
                    {title: 'Home', url: '/home'},
                    {title: 'Settings'},
                    {title: 'Settings_Caps', url: 'settings/caps', active: true}
                ],
                pageTitle: 'Settings_Caps'
            },
            component: () => import('@/views/settings/caps/Caps.vue')
        },
        {
            path: 'settings/landings',
            name: 'settings-landings',
            meta: {
                permissionName: 'settings_landing',
                breadcrumb: [
                    {title: 'Home', url: '/home'},
                    {title: 'Settings'},
                    {title: 'Settings_Landings', url: 'settings/landings', active: true}
                ],
                pageTitle: 'Settings_Landings'
            },
            component: () => import('@/views/settings/landings/Landings.vue')
        },
        {
            path: 'settings/hold',
            name: 'hold',
            meta: {
                permissionName: 'statistic_lead',
                breadcrumb: [
                    {title: 'Home', url: '/home'},
                    {title: 'Settings'},
                    {title: 'Settings_Hold', url: 'settings/hold', active: true}
                ],
                pageTitle: 'Settings_Hold'
            },
            component: () => import('@/views/settings/hold/Hold.vue')
        },
        {
            path: 'settings/geo',
            name: 'geo',
            meta: {
                permissionName: 'settings_geo',
                breadcrumb: [
                    {title: 'Home', url: '/home'},
                    {title: 'Settings'},
                    {title: 'Settings_Geo', url: 'settings/geo', active: true}
                ],
                pageTitle: 'Settings_Geo'
            },
            component: () => import('@/views/settings/geo/Geo.vue')
        },
        {
            path: 'settings/management',
            name: 'management',
            meta: {
                permissionName: 'settings_management',
                breadcrumb: [
                    {title: 'Statistic', url: '/statistic'},
                    {title: 'Settings'},
                    {title: 'Settings_Management', url: 'settings/management', active: true}
                ],
                pageTitle: 'Settings_Management'
            },
            component: () => import('@/views/settings/management/Management.vue')
        },
        {
            path: 'settings/statuses',
            name: 'statuses',
            meta: {
                permissionName: 'settings_status_mapper',
                breadcrumb: [
                    {title: 'Home', url: '/home'},
                    {title: 'Settings'},
                    {title: 'Statuses', url: 'settings/statuses', active: true}
                ],
                pageTitle: 'Statuses'
            },
            component: () => import('@/views/settings/statuses/Statuses.vue')
        },
        {
            path: 'settings/sms/messages',
            name: 'settings-sms-messages',
            meta: {
                permissionName: 'settings_sms_messages',
                breadcrumb: [
                    {title: 'Home', url: '/home'},
                    {title: 'Settings'},
                    {title: 'SMS'},
                    {title: 'Messages', url: 'settings/sms/messages', active: true}
                ],
                pageTitle: 'Messages'
            },
            component: () => import('@/views/settings/sms/messages/Messages.vue')
        },
        {
            path: 'settings/sms/settings',
            name: 'settings-sms-settings',
            meta: {
                permissionName: 'settings_sms_settings',
                breadcrumb: [
                    {title: 'Home', url: '/home'},
                    {title: 'Settings'},
                    {title: 'SMS'},
                    {title: 'Settings', url: 'settings/sms/settings', active: true}
                ],
                pageTitle: 'Messages'
            },
            component: () => import('@/views/settings/sms/settings/Settings.vue')
        },
        {
            path: 'settings/system',
            name: 'system',
            meta: {
                permissionName: 'settings_system',
                breadcrumb: [
                    {title: 'Home', url: '/home'},
                    {title: 'Settings'},
                    {title: 'Settings_System', url: 'settings/system', active: true}
                ],
                pageTitle: 'Settings_System'
            },
            component: () => import('@/views/settings/system/SystemList.vue')
        },
        {
            path: 'check/testlead',
            name: 'lead',
            meta: {
                breadcrumb: [
                    {title: 'Home', url: '/home'},
                    {title: 'Check'},
                    {title: 'TestLead', url: 'check/testlead', active: true}
                ],
                pageTitle: 'TestLead'
            },
            component: () => import('@/views/check/test_lead/TestLead.vue')
        },
        {
            path: 'logs/leads',
            name: 'leads',
            meta: {
                permissionName: 'logs', //todo add permission
                breadcrumb: [
                    {title: 'Home', url: '/home'},
                    {title: 'Logs'},
                    {title: 'Leads', url: 'logs/leads', active: true}
                ],
                pageTitle: 'Logs'
            },
            component: () => import('@/views/logs/leads/Logs.vue')
        },
        {
            path: 'logs/changes',
            name: 'changes',
            meta: {
                permissionName: 'logs', //todo add permission
                breadcrumb: [
                    {title: 'Home', url: '/home'},
                    {title: 'Logs'},
                    {title: 'Changes', url: 'logs/changes', active: true}
                ],
                pageTitle: 'Changes'
            },
            component: () => import('@/views/logs/changes/Changes.vue')
        },
        {
            path: 'campaigns',
            name: 'campaigns',
            meta: {
                permissionName: 'campaign',
                breadcrumb: [
                    {title: 'Home', url: '/home'},
                    {title: 'Campaigns', url: 'campaigns', active: true}
                ],
                pageTitle: 'Campaigns'
            },
            component: () => import('@/views/campaigns/Campaigns.vue')
        },
        {
            path: 'users',
            name: 'users',
            meta: {
                permissionName: 'user',
                breadcrumb: [
                    {title: 'Home', url: '/home'},
                    {title: 'Users', url: 'users', active: true}
                ],
                pageTitle: 'Users'
            },
            component: () => import('@/views/users/Users.vue')
        },
        {
            path: 'Team',
            name: 'team',
            meta: {
                permissionName: 'team',
                breadcrumb: [
                    {title: 'Home', url: '/home'},
                    {title: 'Team', url: 'team', active: true}
                ],
                pageTitle: 'Team'
            },
            component: () => import('@/views/Team.vue')
        },
        {
            path: 'Offers',
            name: 'offers',
            meta: {
                permissionName: 'offers',
                breadcrumb: [
                    {title: 'Home', url: '/home'},
                    {title: 'Offers', url: 'offers', active: true}
                ],
                pageTitle: 'Offers'
            },
            component: () => import('@/views/Offers.vue')
        },
        {
            path: 'Landings',
            name: 'landings',
            meta: {
                permissionName: 'landings',
                breadcrumb: [
                    {title: 'Home', url: '/home'},
                    {title: 'Landings', url: 'landings', active: true}
                ],
                pageTitle: 'Landings'
            },
            component: () => import('@/views/Landings.vue')
        },
        {
            path: 'Domains',
            name: 'domains',
            meta: {
                permissionName: 'domains',
                breadcrumb: [
                    {title: 'Home', url: '/home'},
                    {title: 'Domains', url: 'domains', active: true}
                ],
                pageTitle: 'Domains'
            },
            component: () => import('@/views/domains/Domains.vue')
        },
        {
            path: 'statistic/deposits',
            name: 'deposits',
            meta: {
                permissionName: 'statistic_lead',
                breadcrumb: [
                    {title: 'Home', url: '/home'},
                    {title: 'Deposits', url: 'statistic/deposits', active: true}
                ],
                pageTitle: 'Deposits'
            },
            component: () => import('@/views/statistics/deposits/Deposits.vue')
        },
        {
            path: 'statistic/adv',
            name: 'advertisers',
            meta: {
                permissionName: 'statistic_advertiser',
                breadcrumb: [
                    {title: 'Home', url: '/home'},
                    {title: 'Statistic'},
                    {title: 'Advertiser', url: 'statistic/adv', active: true}
                ],
                pageTitle: 'Advertiser'
            },
            component: () => import('@/views/statistics/advertisers/Advertisers.vue')
        },
        {
            path: 'finance/statistic',
            name: 'finance_statistic',
            meta: {
                permissionName: 'finance_access',
                breadcrumb: [
                    {title: 'Home', url: '/home'},
                    {title: 'Finance'},
                    {title: 'Statistic', url: 'finance/statistic', active: true}
                ],
                pageTitle: 'Statistic'
            },
            component: () => import('@/views/finance/statistic/Statistic.vue')
        },
        {
            path: 'finance/analytics',
            name: 'finance_analytics',
            meta: {
                permissionName: 'finance_analytic_access',
                breadcrumb: [
                    {title: 'Home', url: '/home'},
                    {title: 'Finance'},
                    {title: 'Analytics', url: 'finance/analytics', active: true}
                ],
                pageTitle: 'Analytics'
            },
            component: () => import('@/views/finance/analytics/Analytics.vue')
        },
        // {
        //     path: 'finance/cards',
        //     name: 'finance_cards',
        //     meta: {
        //         permissionName: 'finance_cards',
        //         breadcrumb: [
        //             {title: 'Home', url: '/home' },
        //             {title: 'Finance'},
        //             {title: 'Cards', url: 'finance/cards', active: true}
        //         ],
        //         pageTitle: 'Cards'
        //     },
        //     component: () => import('@/views/finance/cards/Cards.vue')
        // },
        {
            path: 'postbacks',
            name: 'postbacks',
            meta: {
                permissionName: 'postback',
                breadcrumb: [
                    {title: 'Home', url: '/home'},
                    {title: 'Postbacks', url: 'postbacks', active: true}
                ],
                pageTitle: 'Postbacks'
            },
            component: () => import('@/views/postbacks/Postbacks')
        },
        {
            path: 'notifications',
            name: 'notifications',
            meta: {
                permissionName: 'affiliate_bot',
                breadcrumb: [
                    {title: 'Home', url: '/home'},
                    {title: 'Notifications', url: 'notifications', active: true}
                ],
                pageTitle: 'Notifications'
            },
            component: () => import('@/views/notifications/Notifications')
        },
    ]
}

if (!isLeadingSolutionsBrand) {
    MAIN_LAYOUT.children.push(
        {
            path: 'statistic/main',
            name: 'statistic-main',
            meta: {
                permissionName: 'statistic_lead', // todo change
                breadcrumb: [
                    {title: 'Home', url: '/home'},
                    {title: 'Statistic'},
                    {title: 'Statistic_Main', url: 'statistic/main', active: true}
                ],
                pageTitle: 'Statistic_Main'
            },
            component: () => import('@/views/statistics/main/Main.vue')
        },
        {
            path: 'statistic/leads',
            name: 'statistic-leads',
            meta: {
                permissionName: 'statistic_lead',
                breadcrumb: [
                    {title: 'Home', url: '/home'},
                    {title: 'Statistic'},
                    {title: 'Statistic_Leads', url: 'statistic/leads', active: true}
                ],
                pageTitle: 'Statistic_Leads'
            },
            component: () => import('@/views/statistics/leads/Leads.vue')
        },
    )
} else {
    MAIN_LAYOUT.children.push(
        {
            path: 'analytics',
            name: 'analytics',
            meta: {
                permissionName: 'statistic_lead',
                breadcrumb: [
                    {title: 'Home', url: '/home'},
                    {title: 'Analytics', url: 'analytics', active: true}
                ],
                pageTitle: 'Statistic_Leads'
            },
            component: () => import('@/views/analytics/Tab.vue')
        }
    )
}

if (!isLeadingSolutionsDocumentation) {
    PAGE_LAYOUTS.children.push({
        path: 'documentation/:hash',
        name: 'documentation',
        component: () => import('@/views/_documentation/Documentation.vue'),
        children: [
            {
                path: ':api/:method',
                component: () => import('@/views/_documentation/view/Main')
            }
        ]
    })
}


const router = new Router({
    mode: 'history',
    base: '/',
    scrollBehavior() {
        return {x: 0, y: 0}
    },
    routes: [
        {...PAGE_LAYOUTS},
        {...MAIN_LAYOUT},
        {
            path: '*',
            redirect: '/error-404'
        }
    ]

})

router.afterEach(() => {
    const appLoading = document.getElementById('loading-bg')
    if (appLoading) {
        appLoading.style.display = 'none'
    }

    document.title = process.env.VUE_APP_TITLE === undefined ? 'Network' : process.env.VUE_APP_TITLE
})

router.beforeEach(async (to, from, next) => {
    const server = getUseServer()

    if (to.name === 'login' && !store.getters.authError && localStorage.getItem(server.name)) {
        isPermission('statistic_lead.access') ? isLeadingSolutionsBrand ? next('/analytics') : next('/statistic/leads') : next('/home')
    } else if (!localStorage.getItem(server.name) && !(/api|documentation/.test(to.path))) {
        await store.dispatch('AUTH_ERROR')
    }

    next()
})

export default router
